import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { setMessage } from "../message";
import { deleteSuppressionEmailById, getSuppressionEmailList, removeSuppressionEmailsInBulk, removeSuppressionEmailsInBulkByFilter, fetchBulkSuppressionDeleteReports } from "../../../services/suppressionList";

// Async thunk
export const getSuppressionEmails = createAsyncThunk(
    "email/getSuppressionEmails",
    async (payload, thunkAPI) => {
        try {
            const response = await getSuppressionEmailList(payload);
            // Expected backend response:
            // { success: true, data: { records: [], totalPages: 5, currentPage: 1 } }
            console.log("Response from service:", response);
            if (!response.success) {
                return thunkAPI.rejectWithValue(
                    response.data.message || "Failed to fetch records"
                );
            }

            return response.data;
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                "Something went wrong";

            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const deleteSuppressionEmail = createAsyncThunk(
    "email/deleteSuppressionEmail",
    async (payload, thunkAPI) => {
        try {
            const response = await deleteSuppressionEmailById(payload);
            console.log("Delete response from service:", response);
            if (!response.success) {
                return thunkAPI.rejectWithValue(
                    response.data.message || "Failed to delete record"
                );
            }
            return response.data;
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                "Something went wrong";
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    }
)

export const deleteSuppressionEmailsInBulk = createAsyncThunk(
    "email/deleteSuppressionEmailInBulk",
    async (payload, thunkAPI) => {
        try {
            const response = await removeSuppressionEmailsInBulk(payload);
            console.log("Delete response from service:", response);
            if (!response.success) {
                return thunkAPI.rejectWithValue(
                    response.data.message || "Failed to delete record"
                );
            }
            return response.data;
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                "Something went wrong";
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    }
)

export const deleteSuppressionEmailsByFilter = createAsyncThunk(
    'suppressionList/deleteInBulkByFilter',
    async (payload, thunkAPI) => {
        try {
            const response = await removeSuppressionEmailsInBulkByFilter(payload);
            console.log("Delete response from service:", response);
            if (!response.success) {
                return thunkAPI.rejectWithValue(
                    response.message || "Failed to delete record"
                );
            }
            return response?.message || "bulk delete successfully initiated.";
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                "Something went wrong";
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    }
)

// getBulkDeleteReports
export const getBulkSuppressionDeleteReports = createAsyncThunk(
    'suppressionList/getBulkSuppressionDeleteReports',
    async (payload, thunkAPI) => {
        try {
            const response = await fetchBulkSuppressionDeleteReports(payload);
            if (!response.success) {
                return thunkAPI.rejectWithValue(
                    response.data.message || "Failed to fetch records"
                );
            }

            return response.data;
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                "Something went wrong";
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    }
)

const suppressionEmailsSlice = createSlice({
    name: "suppressionEmails",
    initialState: {
        suppressionEmailList: {
            records: [],
            totalPages: 0,
            currentPage: 1,
            loading: false,
            error: null,
        },
        bulkDeleteReportList: {
            records: [],
            totalPages: 0,
            currentPage: 1,
            loading: false,
            error: null,
        },
    },
    reducers: {},
    extraReducers: (builder) => {
        //getSuppressionEmails
        builder
            .addCase(getSuppressionEmails.pending, (state) => {
                state.suppressionEmailList.loading = true;
                state.suppressionEmailList.error = null;
            })
            .addCase(getSuppressionEmails.fulfilled, (state, action) => {
                state.suppressionEmailList.loading = false;
                state.suppressionEmailList.records = action.payload.records || [];
                state.suppressionEmailList.totalPages = action.payload.totalPages || 0;
                state.suppressionEmailList.currentPage = action.payload.currentPage || 1;
            })
            .addCase(getSuppressionEmails.rejected, (state, action) => {
                state.suppressionEmailList.loading = false;
                state.suppressionEmailList.error =
                    action.payload || "Failed to fetch suppression emails";
            });

        //getBulkSuppressionDeleteReports
        builder
            .addCase(getBulkSuppressionDeleteReports.pending, (state) => {
                state.bulkDeleteReportList.loading = true;
                state.bulkDeleteReportList.error = null;
            })
            .addCase(getBulkSuppressionDeleteReports.fulfilled, (state, action) => {
                state.bulkDeleteReportList.loading = false;
                state.bulkDeleteReportList.records = action.payload.records || [];
                state.bulkDeleteReportList.totalPages = action.payload.totalPages || 0;
                state.bulkDeleteReportList.currentPage = action.payload.currentPage || 1;
            })
            .addCase(getBulkSuppressionDeleteReports.rejected, (state, action) => {
                state.bulkDeleteReportList.loading = false;
                state.bulkDeleteReportList.error =
                    action.payload || "Failed to fetch suppression emails";
            });
    },
});

export default suppressionEmailsSlice.reducer;
